import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SparePart, SparePartGroup } from '@ecs-features/result/spare-parts-result/spare-parts.interface';
import { tracking } from '@ecs-helpers';
import { OverlayService } from '@ecs-services/overlay/overlay.service';
import { TranslatePipe } from '@ngx-translate/core';
import { BaseTableModule, SelectableRow, TrackClickDirective } from '@os-components';
import { SewButton, SewHeadline, SewTabBar, SewTabBarItem } from '@sew/ui-components-angular/standalone';
import { PartDetailService } from '../part-table/components/part-detail/part-detail.service';
import { getUniquePartId } from '../part-table/part-table.helper';
import { PartsRecommendationTableComponent } from './components/parts-recommendation-table/parts-recommendation-table.component';
import { PartsRecommendation, RecommendedPart } from './parts-recommendation.interface';

@Component({
    selector: 'ecs-parts-recommendation',
    templateUrl: './parts-recommendation.component.html',
    styleUrls: ['./parts-recommendation.component.scss'],
    imports: [
        SewTabBar,
        SewTabBarItem,
        SewButton,
        SewHeadline,
        PartsRecommendationTableComponent,
        TrackClickDirective,
        TranslatePipe,
        CommonModule,
        BaseTableModule,
    ],
})
export class PartsRecommendationComponent implements OnInit {
    @Input() public showTabBar = true;

    @Input() public set recommendationOrigin(recommendationOrigin: SelectableRow<SparePart> | null) {
        this.recommendation = recommendationOrigin?.rowData.recommendation ?? null;
        this.updateRows();
    }

    @Input() public set checkedRows(checkedRows: SelectableRow<SparePart>[]) {
        this.selectedRowsOfMainTable = checkedRows;
        this.updateRows();
    }

    @Output() public partsSelected = new EventEmitter<void>();

    public columns: string[] | null = null;

    public uncheckedRecommendations: SelectableRow<RecommendedPart>[] = [];
    public checkedRecommendations: SelectableRow<RecommendedPart>[] = [];

    public tracking = tracking;

    private recommendation: PartsRecommendation | null = null;
    private selectedRowsOfMainTable: SelectableRow<SparePart>[] = [];

    private currentlySelectedRecommendations: SelectableRow<RecommendedPart>[] = [];

    public constructor(
        private partDetailService: PartDetailService,
        private overlayService: OverlayService
    ) {}

    public ngOnInit() {
        this.columns = this.getDisplayColumns();
    }

    public onRowSelect(selectedRows: SelectableRow<RecommendedPart>[]): void {
        this.currentlySelectedRecommendations = selectedRows;
    }

    public selectParts() {
        this.partDetailService.checkRowsByRecommendation = {
            group: this.recommendation?.group ?? '',
            rows: this.currentlySelectedRecommendations,
        };
        this.partsSelected.emit();
        this.overlayService.close();
    }

    private updateRows() {
        const allRows = this.recommendation?.recommendedParts.reduce<{
            checked: SelectableRow<RecommendedPart>[];
            unchecked: SelectableRow<RecommendedPart>[];
        }>(
            ({ checked, unchecked }, part) => {
                const isChecked = this.selectedRowsOfMainTable.some(
                    ({ uniqueId }: any) =>
                        uniqueId ===
                        getUniquePartId(
                            { title: (this.recommendation as any).group } as SparePartGroup,
                            part as SparePart,
                            NaN
                        )
                );

                const partRow = {
                    isSelectableRow: !isChecked,
                    isDisabled: false,
                    rowData: {
                        ...part,
                    },
                };

                return {
                    checked: isChecked ? [...checked, partRow] : checked,
                    unchecked: !isChecked ? [...unchecked, partRow] : unchecked,
                };
            },
            {
                checked: [],
                unchecked: [],
            }
        );

        this.checkedRecommendations = allRows?.checked ?? [];

        this.uncheckedRecommendations = allRows?.unchecked ?? [];
    }

    private getDisplayColumns(): string[] {
        return ['itemNumber', 'name', 'materialNumber', 'amount'];
    }
}
